/*
 * A fenced code block (spec 06 §4): its own horizontal scroll inside the body's measure, a 30 px
 * header naming the language with a copy glyph at its end, and a collapse for long blocks so one
 * paste cannot bury a conversation. The copy result is announced in the block's own `role="status"`
 * line, because a copy that silently succeeded is indistinguishable from one that silently failed.
 *
 * The header's controls exist only where somebody can press them. The same tree is rendered to a
 * string for the clipboard's HTML flavour (chat's message/copy.ts) with no surface around it — no render
 * context, no tooltip provider — and a pasted code block with a dead "Copy" button in its corner is
 * chrome that travelled where it means nothing. `useHasRenderSurface` is how the block tells the two
 * apart.
 *
 * The copy reports twice, like every other copy in the product: the block's own `role="status"` line
 * for a reader who is listening, and a toast for an eye — a clipboard write is the one action that
 * leaves the screen exactly as it was.
 *
 * Syntax highlighting is deliberately absent: it would pull a language grammar into the bundle for
 * the first block that names one, and an unknown info string must stay plain text rather than
 * become a guess. The text is rendered verbatim either way.
 */

import { useState } from "react"
import { LiveRegion } from "../../lib/liveRegion.tsx"
import { Button, Tooltip } from "../../ui/index.ts"
import { t } from "../i18n/index.ts"
import { Icon } from "../shell/icons.tsx"
import { useToast } from "../shell/index.ts"
import { useHasRenderSurface } from "./context.ts"
import { Literal } from "./InvisibleText.tsx"

/** Above this many lines the block starts collapsed, showing the first `PREVIEW_LINES`. */
const COLLAPSE_ABOVE = 20
const PREVIEW_LINES = 12

interface CodeBlockProps {
	text: string
	lang?: string | undefined
}

export function CodeBlock({ text, lang }: CodeBlockProps) {
	const [expanded, setExpanded] = useState(false)
	const [status, setStatus] = useState("")
	const interactive = useHasRenderSurface()
	const toast = useToast()

	const lines = text.split("\n")
	const collapsible = lines.length > COLLAPSE_ABOVE
	const shown = collapsible && !expanded ? lines.slice(0, PREVIEW_LINES).join("\n") : text

	async function copy() {
		try {
			await navigator.clipboard.writeText(text)
			setStatus(t("render.code.copied"))
			toast({ kind: "success", key: "render.code.copied" })
		} catch {
			setStatus(t("render.code.copyFailed"))
			toast({ kind: "error", key: "render.code.copyFailed" })
		}
	}

	return (
		<div className="tb-code">
			<div className="tb-code__header">
				<span className="tb-code__lang">{lang && lang !== "" ? lang : t("render.code.plain")}</span>
				{/* A glyph on the dense rung, named for the keyboard and the screen reader and repeated
				    by the tooltip for the eye: the word "Copy" in every block's corner was chrome
				    competing with the code it belongs to. */}
				{interactive ? (
					<Tooltip label={t("render.code.copy")}>
						<Button
							variant="ghost"
							size="sm"
							quiet
							icon
							className="tb-code__copy"
							aria-label={t("render.code.copy")}
							before={<Icon name="copy" size={16} />}
							onClick={() => {
								void copy()
							}}
						/>
					</Tooltip>
				) : null}
			</div>
			{/* A scrollable block must be reachable by keyboard, or its overflow is unreadable
			    without a mouse ([25] G1); that is what the tabindex is for — and a stop the keyboard
			    lands on owes the reader a role and a name, which is the treatment the table in the
			    same body already has (X-B3 D4). The name carries the language wherever the fence
			    declared one: "Code, Go" is what a reader needs before deciding to scroll it. */}
			<pre
				className="tb-code__body"
				role="region"
				// eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
				tabIndex={0}
				aria-label={
					lang !== undefined && lang !== ""
						? t("render.code.regionLang", { lang })
						: t("render.code.region")
				}
			>
				<code>
					<Literal text={shown} />
				</code>
			</pre>
			{collapsible && interactive ? (
				<Button
					variant="ghost"
					size="sm"
					className="tb-code__toggle"
					label={
						expanded
							? t("render.code.collapse")
							: t("render.code.expand", { count: lines.length - PREVIEW_LINES })
					}
					aria-expanded={expanded}
					onClick={() => setExpanded(!expanded)}
				/>
			) : null}
			<LiveRegion label={t("render.code.status")}>{status}</LiveRegion>
		</div>
	)
}
